// src/seo/Layout.jsx
import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { metaConfig, defaultMeta } from "./metaConfig";

/* 뒤 슬래시 제거 */
function normalizePath(p) {
  return p !== "/" && p.endsWith("/") ? p.slice(0, -1) : p;
}

/* 매칭으로 메타 찾기 */
function resolveMeta(path, metaConfigObj, defaultMetaObj) {
  if (metaConfigObj[path]) return metaConfigObj[path];

  const prefixes = [
    ["/company/introduction", "/company/introduction"],
    ["/company/support", "/company/support"],
    ["/company/PrivacyPolicy", "/privacy-policy"],
    ["/privacy-policy", "/privacy-policy"],
    ["/business/aisolution", "/business/aisolution"],
    ["/business/eureka", "/business/eureka"],
    ["/business/trend", "/business/trend"],
    ["/business/eurekagenai", "/business/eurekagenai"],
    ["/business/rpa", "/business/rpa"],
    ["/business/rpaportal", "/business/rpaportal"],
    ["/business/voucher", "/business/voucher"],
    ["/media/news", "/media/news"],
    ["/media/case", "/media/case"],
    ["/media/casebook", "/media/casebook"],
    ["/media/casebooks/detail", "/media/casebooks/detail"],
    ["/media/conference", "/media/conference"],
    ["/media/newsletter", "/media/newsletter"],
    ["/partners/partners", "/partners/partners"],
    ["/sign", "/sign"],
    ["/newslettersubscribe", "/newslettersubscribe"],
    ["/aiexpo", "/aiexpo"],
    ["/aiexpoLLM", "/aiexpoLLM"],
    ["/aiexpoECommerce", "/aiexpoECommerce"],
    ["/aiexpoChatGPT", "/aiexpoChatGPT"],
  ];

  for (const [prefix, key] of prefixes) {
    if (path.startsWith(prefix) && metaConfigObj[key]) {
      return metaConfigObj[key];
    }
  }
  return defaultMetaObj;
}

/* 페이지 스키마가 없을 때 WebPage 폴백 생성 */
function buildFallbackPageSchema({ url, title, description }) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        url,
        name: title,
        description,
        isPartOf: { "@id": "https://rbrain.co.kr/#website" },
        inLanguage: "ko-KR",
      },
    ],
  };
}

export function Layout({ children }) {
  const { pathname } = useLocation();
  const path = normalizePath(pathname);

  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "";

  const url = `${origin}${path || "/"}`;

  const meta = resolveMeta(path, metaConfig, defaultMeta);

  const title = meta.seoTitle || meta.title || defaultMeta.title || "";
  const description =
    meta.seoDescription || meta.description || defaultMeta.description || "";

  const ogImage =
    meta.ogImage ||
    defaultMeta.ogImage ||
    "https://rbrain.co.kr/static/media/no_image.a01579548d0f0fa0f64a.png";

  // 명시적 canonical이 없으면 현재 URL 사용
  const canonical = meta.canonical || url;

  // 1) 사이트 전역 스키마(항상 포함)
  const siteSchema = defaultMeta.schema;

  // 2) 페이지 스키마(있으면 사용, 없으면 WebPage 폴백)
  const pageSchema =
    meta.schema ||
    buildFallbackPageSchema({
      url,
      title,
      description,
    });

  // JSON.stringify 시 불필요한 undefined 제거
  const stringify = (obj) =>
    JSON.stringify(obj, (_, v) => (v === undefined ? undefined : v));

  return (
    <>
      <Helmet
        key={`helmet:${path}`}
        prioritizeSeoTags
        encodeSpecialCharacters={false} // JSON-LD 이스케이프 방지
      >
        {/* 기본 메타 */}
        <title>{title}</title>
        <meta name="description" content={description} />

        {/* og 메타 태그 */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={meta.ogTitle || title} />
        <meta
          property="og:description"
          content={meta.ogDescription || description}
        />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={ogImage} />

        {/* 캐노니컬 */}
        <link rel="canonical" href={canonical} />

        {/* 스키마 */}
        <script type="application/ld+json" data-rh="true" id="ldjson-site">
          {stringify(siteSchema)}
        </script>

        <script type="application/ld+json" data-rh="true" id="ldjson-page">
          {stringify(pageSchema)}
        </script>
      </Helmet>

      {children}
    </>
  );
}

export default Layout;
